'use client';

import { createContext, useContext, useEffect, useState } from 'react';
import { createClient } from '@/lib/supabase/client';
import { derivePermissions, WorkspaceRole, WorkspacePermissions } from '@/lib/useWorkspaceRole';

const AuthContext = createContext<any>({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<any>(null);
  const [session, setSession] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [workspaceRole, setWorkspaceRole] = useState<WorkspaceRole>(null);
  const [workspacePermissions, setWorkspacePermissions] = useState<Omit<WorkspacePermissions, 'isLoading'>>(
    derivePermissions(null)
  );
  const supabase = createClient();

  // Fetch workspace role for the active transformation
  const fetchWorkspaceRole = async (userId: string) => {
    try {
      const { data: profile } = await supabase
        .from('user_profiles')
        .select('organization_id, role')
        .eq('id', userId)
        .single();

      if (!profile?.organization_id) return;

      const { data: tx } = await supabase
        .from('transformations')
        .select('id')
        .eq('organization_id', profile.organization_id)
        .order('created_at', { ascending: false })
        .limit(1)
        .single();

      if (!tx?.id) return;

      const { data: member } = await supabase
        .from('workspace_members')
        .select('workspace_role')
        .eq('transformation_id', tx.id)
        .eq('user_id', userId)
        .eq('is_active', true)
        .single();

      let resolvedRole: WorkspaceRole;
      if (member?.workspace_role) {
        resolvedRole = member.workspace_role as WorkspaceRole;
      } else if (profile.role === 'admin') {
        resolvedRole = 'admin';
      } else if (profile.role === 'adoption_lead') {
        resolvedRole = 'delivery_lead';
      } else {
        resolvedRole = 'viewer';
      }

      setWorkspaceRole(resolvedRole);
      setWorkspacePermissions(derivePermissions(resolvedRole));
    } catch { /* silent */ }
  };

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) fetchWorkspaceRole(session.user.id);
      setLoading(false);
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) fetchWorkspaceRole(session.user.id);
      else {
        setWorkspaceRole(null);
        setWorkspacePermissions(derivePermissions(null));
      }
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signUp = async (email: string, password: string, metadata: any = {}) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          full_name: metadata?.fullName || '',
          avatar_url: metadata?.avatarUrl || '',
          role: metadata?.role || 'viewer',
        },
        emailRedirectTo: `${process.env.NEXT_PUBLIC_SITE_URL || window.location.origin}/auth/callback`,
      },
    });
    if (error) throw error;
    return data;
  };

  const signIn = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    if (error) throw error;
    return data;
  };

  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
  };

  const getCurrentUser = async () => {
    const {
      data: { user },
      error,
    } = await supabase.auth.getUser();
    if (error) throw error;
    return user;
  };

  const isEmailVerified = () => {
    return user?.email_confirmed_at !== null;
  };

  const getUserProfile = async () => {
    if (!user) return null;
    const { data, error } = await supabase
      .from('user_profiles')
      .select('*, organizations(*)')
      .eq('id', user.id)
      .single();
    if (error) throw error;
    return data;
  };

  const value = {
    user,
    session,
    loading,
    workspaceRole,
    workspacePermissions,
    signUp,
    signIn,
    signOut,
    getCurrentUser,
    isEmailVerified,
    getUserProfile,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
